/* eslint-disable max-classes-per-file */
import { Injectable, NgZone, inject } from "@angular/core";
import _, { flatten, orderBy } from "lodash";
import { BehaviorSubject, Observable, Subject } from "rxjs";

import {
    debounceTime,
    filter,
    map,
    repeat,
    skip,
    skipUntil,
    takeUntil
} from "rxjs/operators";
import { IMessageModel } from "~app/common/components/message-panel/message-panel.models";
import { SagaStatusEventMessage } from "~app/common/events/saga/SagaStatusEventMessage";
import { SagaStatusEventMessageArbetsorderData } from "~app/common/events/saga/SagaStatusEventMessageArbetsorderData";
import { VisibleSagas } from "~app/common/models/constants";
import { ApiClientService } from "~app/common/services/api-client.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { ArbetsorderWorkEventMessage } from "../events/ArbetsorderWorkEventMessage";
import { ArbetsorderAlertService } from "./arbetsorder-alert.service";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderMonitorService {
    private readonly apiClient = inject(ApiClientService);
    private readonly eventService = inject(EventService);
    private readonly alertService = inject(ArbetsorderAlertService);
    private readonly zone = inject(NgZone);

    private readonly isWorking$ = new BehaviorSubject<boolean>(false);
    private readonly workInProgress$ = new BehaviorSubject<ArbetsorderWorkEventMessage[]>([]);
    private readonly alerts$ = new BehaviorSubject<IMessageModel[]>([]);

    private readonly unsubscribe$ = new Subject<void>();
    private readonly pulse$ = new Subject<void>();
    private readonly guard$ = new Subject<void>();

    private currentArbetsorderId: number;
    private incomingWork: ArbetsorderWorkEventMessage[] = [];
    private currentWork: ArbetsorderWorkEventMessage[] = [];

    start(arbetsorderId: number): void {
        if (arbetsorderId === this.currentArbetsorderId) {
            return;
        }

        if (this.currentArbetsorderId) {
            this.unsubscribe$.next();
        }

        this.currentArbetsorderId = arbetsorderId;

        this.getCurrentWork(arbetsorderId).subscribe(current => {
            this.currentWork = current;
            this.eventService
                .subscribeEvent<ArbetsorderWorkEventMessage>(ArbetsorderWorkEventMessage.$type)
                .pipe(filter(x => x.arbetsorderId === arbetsorderId))
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(e => this.onEventReceived(e));

            this.pulse$
                .pipe(debounceTime(100))
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(() => this.onPulse());

            this.guard$
                .pipe(debounceTime(5000))
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(() => this.onGuard());

            this.onPulse();

            this.eventService.subscribeAllArbetsorderSaga()
                .pipe(takeUntil(this.unsubscribe$))
                .pipe(filter(event => event.data.arbetsorderId === arbetsorderId))
                .subscribe(e => this.onSagaEventReceived(e));
        });

        this.alertService.getAlerts(arbetsorderId, this.unsubscribe$).subscribe(this.alerts$);
    }

    refreshAlerts(): void {
        this.alertService.getAlerts(this.currentArbetsorderId, this.unsubscribe$).subscribe(this.alerts$);
    }

    stop(): void {
        this.currentArbetsorderId = undefined;
        this.unsubscribe$.next();

        this.incomingWork = [];
        this.currentWork = [];
        this.onPulse();
    }

    connect(keepOpen = false): ArbetsorderMonitor {
        const unsubscribe$ = new Subject<void>();
        const monitor = new ArbetsorderMonitor(unsubscribe$, this.alerts$);

        this.isWorking$
            .pipe(takeUntil(unsubscribe$))
            .subscribe(monitor.isWorking$);

        this.workInProgress$
            .pipe(takeUntil(unsubscribe$))
            .subscribe(monitor.workInProgress$);

        if (!keepOpen) {
            // Hänglsen och livrem om "utvecklaren" glömmer att göra disconnect på ArbetsorderMonitor
            this.unsubscribe$.subscribe(() => {
                // eslint-disable-next-line sonarjs/deprecation
                if (!unsubscribe$.isStopped) {
                    unsubscribe$.next();
                    unsubscribe$.complete();
                }
            });
        }

        return monitor;
    }

    private getCurrentWork(arbetsorderId: number): Observable<ArbetsorderWorkEventMessage[]> {
        return this.apiClient.get(`work/arbetsorder/${arbetsorderId}`, false);
    }

    private onEventReceived(message: ArbetsorderWorkEventMessage) {
        this.incomingWork.push(message);
        this.pulse$.next();
    }

    private onSagaEventReceived(message: SagaStatusEventMessage<SagaStatusEventMessageArbetsorderData>) {
        if (!VisibleSagas.Alla.some(x => x === message.sagaType)) {
            return;
        }

        const eventMessage = new ArbetsorderWorkEventMessage();
        eventMessage.arbetsorderId = message.data.arbetsorderId;
        eventMessage.workDescription = message.message;
        eventMessage.work = message.sagaType;
        eventMessage.status = message.active ? ArbetsorderWorkEventMessage.pending : ArbetsorderWorkEventMessage.completed;
        eventMessage.workRef = message.sagaId;

        const aktuelltMeddelande = this.currentWork.find(i => i.workRef === message.sagaId);
        const index = this.currentWork.indexOf(aktuelltMeddelande);

        if (aktuelltMeddelande !== undefined) {
            this.currentWork.splice(index, 1);
        }

        this.incomingWork.push(eventMessage);
        this.pulse$.next();
    }

    private onPulse() {
        const current = this.collect();
        const isWorking = current.some(x => x.status === ArbetsorderWorkEventMessage.pending);
        if (isWorking !== this.isWorking$.value) {
            this.isWorking$.next(isWorking);
        }

        this.workInProgress$.next(current);
        this.zone.runOutsideAngular(() => {
            this.guard$.next();
        });
    }

    private onGuard(): void {
        if (!this.isWorking$.value) {
            return;
        }

        if (!this.currentArbetsorderId) {
            return;
        }

        this.getCurrentWork(this.currentArbetsorderId).subscribe(current => {
            if (!current.length) {
                this.currentWork = [];
                this.onPulse();
                return;
            }
            this.zone.runOutsideAngular(() => {
                this.guard$.next();
            });
        });
    }

    private collect() {
        // Om vi fått flera events i incoming på samma workRef så tar vi senaste
        // Kan hända om vi får event både på användaren och på arbetsordern exempelvis
        const incoming = _.chain(this.incomingWork)
            .groupBy(x => x.workRef)
            .map(x => _.orderBy(x, y => y.messageTimestamp, "desc")[0])
            .value();

        this.incomingWork = [];

        let current = this.currentWork;

        orderBy(incoming, message => message.messageTimestamp)
            .forEach(message => {
                const existing = current.find(x => x.workRef === message.workRef);
                switch (message.status) {
                    case ArbetsorderWorkEventMessage.pending:
                    case ArbetsorderWorkEventMessage.error:
                        if (existing) {
                            existing.status = message.status;
                            existing.messageTimestamp = message.messageTimestamp;
                            return;
                        }
                        current.push(message);
                        break;
                    case ArbetsorderWorkEventMessage.completed:
                        if (existing) {
                            current = current.filter(x => x !== existing);
                        }
                        break;
                }
            });
        this.currentWork = current;
        return current;
    }
}

export class ArbetsorderMonitor {
    isWorking$ = new BehaviorSubject<boolean>(false);
    workInProgress$ = new BehaviorSubject<ArbetsorderWorkEventMessage[]>([]);
    alerts$ = new BehaviorSubject<IMessageModel[]>([]);

    private readonly systemalerts$: BehaviorSubject<IMessageModel[]>;
    private readonly messageFactories: (() => IMessageModel[])[] = [];

    constructor(private readonly unsubscribe$: Subject<void>, systemalerts$: BehaviorSubject<IMessageModel[]>) {
        this.systemalerts$ = systemalerts$;

        systemalerts$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.rebuildAlerts());
    }

    disconnect(): void {
        this.unsubscribe$.next();
    }

    getWork(namedWork: string, onCompleted?: () => void): BehaviorSubject<boolean> {
        const match = (message: ArbetsorderWorkEventMessage): boolean => message.work?.indexOf(namedWork) !== -1 && message.status === ArbetsorderWorkEventMessage.pending;

        const initialState = this.workInProgress$.value.some(match);
        const subject = new BehaviorSubject<boolean>(initialState);

        this.workInProgress$
            .pipe(map(x => x.find(match)))
            .pipe(map(x => !!x))
            .subscribe(subject);

        if (onCompleted) {
            const working$ = new Subject<void>();
            const reset$ = new Subject<void>();

            subject
                .pipe(
                    skip(1),
                    takeUntil(working$),
                    repeat({ delay: () => reset$ })
                )
                .subscribe(working => {
                    if (working) {
                        working$.next();
                    }
                });

            subject
                .pipe(
                    skipUntil(working$),
                    repeat({ delay: () => reset$ })
                )
                .subscribe(working => {
                    if (!working) {
                        onCompleted();
                        reset$.next();
                    }
                });
        }

        return subject;
    }

    registerAlertMessageFactory(factory: () => IMessageModel[]) {
        this.messageFactories.push(factory);
        this.rebuildAlerts();
    }

    rebuildAlerts(): void {
        const systemSources = this.systemalerts$.value;
        const registeredSources = flatten(this.messageFactories.map(x1 => x1()));

        this.alerts$.next([
            ...systemSources,
            ...registeredSources
        ]);
    }
}
